import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import PageContainer from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import {
  Bell, Palette, Hammer, FileCheck2, Shield, ListChecks, Lock,
} from 'lucide-react';
import { useRoleGates } from '@/hooks/useRoleGates';
import { createNotification } from '@/utils/notificationUtils';
import BackButton from '@/components/layout/BackButton';
import WasteFactorsCard from '@/components/settings/WasteFactorsCard';
import FinishLevelsCard from '@/components/settings/FinishLevelsCard';
import ScrewPreferencesCard from '@/components/settings/ScrewPreferencesCard';
import LaborCostCard from '@/components/settings/LaborCostCard';
import StaleThresholdCard from '@/components/settings/StaleThresholdCard';
import SetupChecklist from '@/components/settings/SetupChecklist';

/**
 * Hub Impostazioni — Tabs riorganizzati per area funzionale.
 *
 * Tab visibili:
 *  - Tecniche cantiere: sfridi, finiture Q1-Q4, viti, manodopera
 *  - Preventivi: soglia "stale" + futuro IVA/validità/pagamenti
 *  - Notifiche: toggle email/push (locali)
 *  - Aspetto: tema chiaro/scuro
 *
 * Pagine specialistiche (Organizzazione, Membri, Fornitori, Abbonamento,
 * Importa listino) restano separate e sono raggiungibili dal sidebar.
 *
 * Deep-link supportato: `/settings?tab=technical|estimates|notifications|appearance`.
 */
type SettingsTab = 'technical' | 'estimates' | 'notifications' | 'appearance';
const VALID_TABS: SettingsTab[] = ['technical', 'estimates', 'notifications', 'appearance'];

const Settings = () => {
  const { canEditTechnicalSettings } = useRoleGates();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as SettingsTab | null;
  const initialTab: SettingsTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'technical';
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    marketingEmails: false,
  });
  const [loading, setLoading] = useState(false);

  // Sincronizza tab attivo con URL (back/forward browser).
  useEffect(() => {
    const t = searchParams.get('tab') as SettingsTab | null;
    if (t && VALID_TABS.includes(t) && t !== activeTab) {
      setActiveTab(t);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (next: string) => {
    const t = next as SettingsTab;
    setActiveTab(t);
    setSearchParams({ tab: t }, { replace: true });
  };

  const handleSettingChange = (key: keyof typeof settings, value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 600));
      toast.success('Preferenze notifiche salvate');
      await createNotification(
        'Impostazioni aggiornate',
        'Le tue preferenze sono state salvate.',
        'success',
      );
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    if (!user) return;
    try {
      await createNotification(
        'Notifica di test',
        'Questa è una notifica di test.',
        'info',
      );
      toast.success('Notifica di test creata');
    } catch (error) {
      console.error('Error creating test notification:', error);
      toast.error('Errore durante la creazione della notifica');
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <PageContainer>
            <div className="mb-4">
              <BackButton />
            </div>
            <div className="mb-6">
              <h1 className="text-3xl font-bold tracking-tight">Impostazioni</h1>
              <p className="text-muted-foreground mt-1">
                Configurazioni tecniche e preferenze dell'applicazione
              </p>
            </div>

            <div className="max-w-4xl space-y-6">
              {/* Setup checklist sempre in alto: linka all'utente i passi mancanti */}
              <SetupChecklist />

              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="technical" className="gap-1.5">
                    <Hammer className="h-4 w-4" />
                    <span className="hidden sm:inline">Tecniche cantiere</span>
                    <span className="sm:hidden">Tecniche</span>
                  </TabsTrigger>
                  <TabsTrigger value="estimates" className="gap-1.5">
                    <FileCheck2 className="h-4 w-4" />
                    Preventivi
                  </TabsTrigger>
                  <TabsTrigger value="notifications" className="gap-1.5">
                    <Bell className="h-4 w-4" />
                    <span className="hidden sm:inline">Notifiche</span>
                  </TabsTrigger>
                  <TabsTrigger value="appearance" className="gap-1.5">
                    <Palette className="h-4 w-4" />
                    Aspetto
                  </TabsTrigger>
                </TabsList>

                {/* ============================================================ */}
                {/* TAB 1: TECNICHE CANTIERE                                     */}
                {/* ============================================================ */}
                <TabsContent value="technical" className="mt-6 space-y-6">
                  <Card className="border-dashed bg-muted/30">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <ListChecks className="h-4 w-4" />
                        Cosa configuro qui
                      </CardTitle>
                      <CardDescription>
                        Costo manodopera orario, sfridi per categoria, livelli di finitura (Q1-Q4)
                        con BOM, preferenze auto-assegnazione viti. Sono i parametri che il
                        configuratore usa per calcolare €/m² in modo realistico.
                      </CardDescription>
                    </CardHeader>
                  </Card>

                  {!canEditTechnicalSettings && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-sm">
                      <Lock className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                      <div>
                        <strong className="text-amber-900">Visualizzazione in sola lettura.</strong>
                        <span className="text-amber-800 ml-1">
                          Le impostazioni tecniche cantiere sono condivise a livello organizzazione e impattano i calcoli di tutto il team.
                          Solo i ruoli <strong>Supervisore</strong>, <strong>Amministratore</strong> e <strong>Titolare</strong> possono modificarle.
                        </span>
                      </div>
                    </div>
                  )}

                  <LaborCostCard />
                  <WasteFactorsCard />
                  <FinishLevelsCard />
                  <ScrewPreferencesCard />
                </TabsContent>

                {/* ============================================================ */}
                {/* TAB 2: PREVENTIVI                                            */}
                {/* ============================================================ */}
                <TabsContent value="estimates" className="mt-6 space-y-6">
                  <Card className="border-dashed bg-muted/30">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <ListChecks className="h-4 w-4" />
                        Cosa configuro qui
                      </CardTitle>
                      <CardDescription>
                        Soglia oltre cui i preventivi sono considerati "vecchi" (prezzi
                        disallineati). Aliquote IVA e validità offerta sono gestite a livello
                        di Organizzazione.
                      </CardDescription>
                    </CardHeader>
                  </Card>

                  <StaleThresholdCard />
                </TabsContent>

                {/* ============================================================ */}
                {/* TAB 3: NOTIFICHE                                             */}
                {/* ============================================================ */}
                <TabsContent value="notifications" className="mt-6 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5" />
                        Preferenze notifiche
                      </CardTitle>
                      <CardDescription>
                        Scegli quali notifiche ricevere e dove. La provider email reale verrà
                        attivata in una prossima versione.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="email-notifications">Notifiche Email</Label>
                          <p className="text-sm text-muted-foreground">
                            Ricevi notifiche via email per aggiornamenti importanti
                          </p>
                        </div>
                        <Switch
                          id="email-notifications"
                          checked={settings.emailNotifications}
                          onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="push-notifications">Notifiche Push</Label>
                          <p className="text-sm text-muted-foreground">
                            Ricevi notifiche push nel browser
                          </p>
                        </div>
                        <Switch
                          id="push-notifications"
                          checked={settings.pushNotifications}
                          onCheckedChange={(checked) => handleSettingChange('pushNotifications', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="marketing-emails">Email Marketing</Label>
                          <p className="text-sm text-muted-foreground">
                            Ricevi aggiornamenti su nuove funzionalità e offerte
                          </p>
                        </div>
                        <Switch
                          id="marketing-emails"
                          checked={settings.marketingEmails}
                          onCheckedChange={(checked) => handleSettingChange('marketingEmails', checked)}
                        />
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between gap-3">
                        <Button onClick={handleTestNotification} variant="outline" size="sm">
                          Crea notifica di test
                        </Button>
                        <Button onClick={handleSaveSettings} disabled={loading} size="sm">
                          {loading ? 'Salvataggio...' : 'Salva preferenze'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Account
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Label>Sicurezza account</Label>
                        <p className="text-sm text-muted-foreground">
                          Cambio password e gestione 2FA in arrivo nella prossima versione.
                        </p>
                        <Button variant="outline" disabled>
                          Cambia Password
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ============================================================ */}
                {/* TAB 4: ASPETTO                                                */}
                {/* ============================================================ */}
                <TabsContent value="appearance" className="mt-6 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Palette className="h-5 w-5" />
                        Aspetto
                      </CardTitle>
                      <CardDescription>
                        Personalizza l'aspetto dell'interfaccia.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="dark-mode">Modalità Scura</Label>
                          <p className="text-sm text-muted-foreground">
                            Attiva il tema scuro per ridurre l'affaticamento visivo
                          </p>
                        </div>
                        <Switch
                          id="dark-mode"
                          checked={theme === 'dark'}
                          onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </PageContainer>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Settings;
